// External Dependencies
import { EstimateShowResponse } from '@presto-assistant/api_types/api/admin/estimates';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Box from '@mui/material/Box';

// Internal Dependencies
import {
  DetailCard, EnhancedLink, ShowPageDataDisplay,
} from 'components/shared';
import { useGetDistrictById } from 'gql/queries/districts';

// Local Dependencies
import PurchaseOrderCard from './PurchaseOrderCard';

// Local Typings
interface EstimateInfoContentProps {
  data: EstimateShowResponse;
}

// Component Definition
const EstimateInfoContent = ({
  data,
}: EstimateInfoContentProps): JSX.Element => {
  const {
    estimate,
    invoice,
    purchaseOrders,
    status,
    totalInCents,
  } = data;

  const navigate = useNavigate();

  const {
    data: districtData,
  } = useGetDistrictById(estimate.districtId ?? undefined);

  const navigateToDistrictShowPage = useCallback(() => {
    if (estimate.districtId || invoice?.districtId) {
      navigate(`/admin_portals/${estimate.districtId || invoice?.districtId}`);
    }
  }, [navigate, estimate, invoice]);

  const navigateToInvoiceShowPage = useCallback(() => {
    if (invoice) {
      navigate(`/invoices/${invoice.id}`);
    }
  }, [navigate, invoice]);

  const latestPurchaseOrder = purchaseOrders[0];

  return (
    <>
      <DetailCard
        avatarAriaLabel="Estimate info"
        avatarIcon={<AttachMoneyIcon />}
        content={(
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
            sx={{ gapX: 3, gapY: 2 }}
          >
            <ShowPageDataDisplay
              label="Estimate Number"
              value={estimate.estimateNumber}
            />

            <ShowPageDataDisplay
              label="Estimate Date"
              type="date"
              value={estimate.createdAt.toString()}
            />

            <ShowPageDataDisplay
              label="Admin Portal"
              value={districtData?.districtById.label ? (
                <EnhancedLink onClick={navigateToDistrictShowPage}>
                  {districtData?.districtById.label}
                </EnhancedLink>
              ) : null}
            />

            {invoice && (
              <ShowPageDataDisplay
                label="Associated Invoice"
                value={invoice.invoiceNumber ? (
                  <Box
                    alignItems="center"
                    display="flex"
                  >
                    <EnhancedLink onClick={navigateToInvoiceShowPage}>
                      {invoice.invoiceNumber}
                    </EnhancedLink>
                  </Box>
                ) : null}
              />
            )}

            <ShowPageDataDisplay
              label="Total"
              type="currency"
              value={totalInCents}
            />

            <ShowPageDataDisplay
              label="Status"
              value={status}
            />
          </Box>
        )}
        subheader={estimate.estimateNumber.toString()}
        title="Estimate"
      />

      {latestPurchaseOrder && (
        <Box mt={2}>
          <PurchaseOrderCard
            estimateId={estimate.id}
            purchaseOrder={latestPurchaseOrder}
          />
        </Box>
      )}
    </>
  );
};

export default EstimateInfoContent;
